import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Category, MenuItem } from "@/model/entities";
import { Button } from "@/components/ui/button";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { Edit, Trash2, Plus, Eye, EyeOff } from "lucide-react";

interface Props {
  menuItems: MenuItem[];
  categories: Category[];
  onEdit?: (item: MenuItem) => void;
  onDelete?: (item: MenuItem) => void;
  onAdd?: () => void;
  onToggleAvailability?: (item: MenuItem) => void;
}

export const MenuItemsTable: React.FC<Props> = ({
  menuItems,
  categories,
  onEdit,
  onDelete,
  onAdd,
  onToggleAvailability,
}) => {
  const { t } = useTranslation("menu");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MenuItem | null>(null);

  const handleDeleteClick = (it: MenuItem) => {
    setItemToDelete(it);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete && onDelete) onDelete(itemToDelete);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <Button onClick={() => onAdd?.()}>
          <Plus className="mr-2" />
          {t("addMenuItem", "הוספת מנה")}
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">{t("name", "שם מנה")}</TableHead>
            <TableHead className="text-right">
              {t("category", "קטגוריה")}
            </TableHead>
            <TableHead className="text-right">
              {t("price", "מחיר (₪)")}
            </TableHead>
            <TableHead className="text-right">
              {t("actions", "פעולות")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {menuItems.map((item: MenuItem) => {
            const hasCategoryName =
              typeof (item as unknown as { category?: unknown }).category ===
              "string";
            // Try to resolve category name from categories cache using categoryId
            const categoryObj = categories?.find(
              (c) => c.id === item.categoryId,
            );
            const resolvedCategoryName = hasCategoryName
              ? (item as unknown as { category: string }).category
              : (categoryObj?.name ?? item.categoryId ?? "-");
            return (
              <TableRow key={item.id}>
                <TableCell className="text-right">{item.name}</TableCell>
                <TableCell className="text-right">
                  {resolvedCategoryName}
                </TableCell>
                <TableCell className="text-right">{item.price}</TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-row gap-2 justify-start">
                                        <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onToggleAvailability?.(item)}
                      aria-label={
                        item.isAvailable
                          ? t("hideAria", {
                              name: item.name,
                              defaultValue: `הסתר ${item.name}`,
                            })
                          : t("unhideAria", {
                              name: item.name,
                              defaultValue: `הצג ${item.name}`,
                            })
                      }
                      title={
                        item.isAvailable
                          ? t("hide", "הסתר מהתפריט")
                          : t("unhide", "הצג בתפריט")
                      }
                    >
                      {item.isAvailable ? (
                        <Eye className="size-4" />
                      ) : (
                        <EyeOff className="size-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit?.(item)}
                      aria-label={t("editAria", {
                        name: item.name,
                        defaultValue: `ערוך ${item.name}`,
                      })}
                    >
                      <Edit className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(item)}
                      aria-label={t("deleteAria", {
                        name: item.name,
                        defaultValue: `מחק ${item.name}`,
                      })}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("deleteTitle", "מחיקת מנה")}
        description={t("deleteConfirm", {
          name: itemToDelete?.name ?? "",
          defaultValue: `האם אתה בטוח שברצונך למחוק את המנה "${itemToDelete?.name}"? פעולה זו אינה הפיכה.`,
        })}
      />
    </>
  );
};
